import { inject } from '@angular/core';
import { ApiQuery } from '@kinet-open-network/client/core/api-client';
import {
  callApiMethod,
  setError,
  setLoaded,
  setLoading,
  withExport,
  withResourceState,
} from '@kinet-open-network/client/shared/data-access/state';
import {
  DocumentMassDownloadFormat,
  IDocumentActionData,
  IDocumentSignatureReminderSettings,
  ISendDocumentOptions,
  SendDocMethod,
} from '@kinet-open-network/shared/data-access/model';
import { patchState, signalStore, withMethods } from '@ngrx/signals';
import { firstValueFrom } from 'rxjs';
import { DocumentsService } from '../services/documents.service';

/**
 * Store for the cross-customer documents feature (all documents from all
 * customers) – backs both the list and the detail routes.
 *
 * Loaded on demand (the list triggers it from `queryChange`, the detail via
 * `load`) so that opening a single document does not fetch the whole list. The
 * per-customer documents tab uses its own `CustomerDocumentsStore` (in
 * `@kinet-open-network/client/customers/data-access`).
 */
export const DocumentsStore = signalStore(
  { protectedState: false },
  withResourceState(DocumentsService, {
    loadOnInit: false,
    query: { initial: { sort: ['-createdAt'] } },
  }),
  withExport(DocumentsService),
  withMethods((store, service = inject(DocumentsService)) => ({
    generate: (id: string) => {
      patchState(store, setLoading());

      return callApiMethod(() => service.generate(id), {
        next: () => patchState(store, setLoaded()),
        error: (err) => patchState(store, setError(err)),
      });
    },
    batchAction: (
      data: Pick<IDocumentActionData, 'action'>,
      query?: ApiQuery | null,
    ) => {
      patchState(store, setLoading());

      return callApiMethod(() => service.batchAction(data, query), {
        next: () => patchState(store, setLoaded()),
        error: (err) => patchState(store, setError(err)),
      });
    },
    massDownload: (
      format: DocumentMassDownloadFormat,
      query?: ApiQuery | null,
    ) => {
      patchState(store, setLoading());

      return service.massDownload(format, query).then(
        () => patchState(store, setLoaded()),
        (err) => patchState(store, setError(err)),
      );
    },
    signDocument: (id: string, signature: string) => {
      patchState(store, setLoading());

      return callApiMethod(() => service.signDocument(id, signature), {
        next: (doc) => patchState(store, { model: doc }, setLoaded()),
        error: (err) => patchState(store, setError(err)),
      });
    },
    markAsSigned: (id: string) => {
      patchState(store, setLoading());

      return callApiMethod(() => service.markAsSigned(id), {
        next: (doc) => patchState(store, { model: doc }, setLoaded()),
        error: (err) => patchState(store, setError(err)),
      });
    },
    send: async (
      id: string,
      method: SendDocMethod,
      options?: ISendDocumentOptions,
    ) => {
      patchState(store, setLoading());

      try {
        await firstValueFrom(service.sendDocument(id, method, options));
        patchState(store, setLoaded());
      } catch (error) {
        patchState(store, setError(error));
        throw error;
      }
    },
    updateReminderSettings: async (
      id: string,
      settings: IDocumentSignatureReminderSettings | null,
    ) => {
      patchState(store, setLoading());

      try {
        await firstValueFrom(service.updateReminderSettings(id, settings));
        patchState(store, setLoaded());
      } catch (error) {
        patchState(store, setError(error));
        throw error;
      }
    },
  })),
);
